import { NgClass } from '@angular/common'
import { AfterViewInit, Component, ElementRef, inject, input } from '@angular/core'

import { TranslatePipe } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular'
import type { FeedbackFormData, FeedbackInternalOptions, Integration } from '@sentry/core'
import { Button } from 'primeng/button'
import { distinctUntilChanged, takeUntil } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

import { ApiService } from '@ti/core/api'
import { bufferToDataUrl, downsizeImage, injectDestroy$ } from '@ti/core/common/utils'
import { Feedback2IconComponent } from '@ti/core/icons/feedback-2'

import { LanguageService } from '../../../i18n'
import { TooltipDirective } from '../../directives'

type SentryFeedbackIntegration = Integration & {
  attachTo: (el: HTMLElement, options: Partial<FeedbackInternalOptions>) => void
  remove: () => void
}

@Component({
  selector: 'app-feedback-button',
  templateUrl: 'feedback-button.component.html',
  styles: `
    .feedback-button {
      --p-button__bg-color: var(--color-gray-900);
      --p-button__border-color: var(--color-gray-900);
      --p-button__hover-bg-color: var(--color-gray-900);
      --p-button__hover-border-color: var(--color-gray-900);
      --p-button__active-bg-color: var(--color-gray-900);
      --p-button__active-border-color: var(--color-gray-900);
    }

    .compact {
      .icon {
        margin: 0;
      }
    }
  `,
  imports: [Feedback2IconComponent, Button, TranslatePipe, NgClass, TooltipDirective],
})
export class FeedbackButtonComponent implements AfterViewInit {
  public readonly isCompact = input(false)
  public readonly isFloating = input(false)

  protected readonly api = inject(ApiService)
  protected readonly elementRef = inject(ElementRef)
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  protected readonly feedbackOptions = {
    isNameRequired: true,
    isEmailRequired: true,
    showBranding: false,
    autoInject: false,
    arguments: [],
    colorScheme: 'light',
    themeLight: {
      accentBackground: 'var(--color-primary-500)', // color-primary-500
    },
  } as Partial<FeedbackInternalOptions>

  public async ngAfterViewInit() {
    let currentFeedbackIntegration: SentryFeedbackIntegration

    this.languageService.current$
      .pipe(debounceTime(512), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(async () => {
        // Remove the previous integration instance
        this.cleanupIntegration(currentFeedbackIntegration)

        const textConfiguration = await this.getTextConfig()
        currentFeedbackIntegration = Sentry.feedbackIntegration()
        currentFeedbackIntegration.attachTo(this.elementRef.nativeElement, {
          ...textConfiguration,
          ...this.feedbackOptions,
          onFormOpen: this.onFeedbackFormOpened,
          onSubmitSuccess: (data: FeedbackFormData) =>
            this.sendCustomFeedbackNotif(data).catch((e) =>
              console.error(`Cannot send custom feedback notif`, e),
            ),
        })
      })

    // Remove form after the component is destroyed
    this.destroy$.subscribe(() => {
      this.cleanupIntegration(currentFeedbackIntegration)
    })
  }

  protected async sendCustomFeedbackNotif(data: FeedbackFormData) {
    const attachments = Array.isArray(data.attachments)
      ? await Promise.all(
          data.attachments.map(async (attachment) => {
            let data =
              typeof attachment.data !== 'string'
                ? await downsizeImage(await bufferToDataUrl(attachment.data), {
                    maxSize: 1920,
                    quality: 0.75,
                    saveFormat: attachment.contentType,
                  })
                : attachment.data

            // Remove DataURL prefix
            if (data.includes(';base64,')) {
              data = data.split(';base64,')[1]
            }

            return { ...attachment, data }
          }),
        )
      : []

    await this.api.feedback.send({ ...data, attachments })
  }

  protected cleanupIntegration(currentFeedbackIntegration: SentryFeedbackIntegration) {
    if (currentFeedbackIntegration) {
      try {
        currentFeedbackIntegration?.remove()
      } catch {
        return
      }
    }
  }

  protected async getTextConfig() {
    const translations = await this.getTranslations()
    return {
      formTitle: translations.title,
      cancelButtonLabel: translations.cancelButton,
      submitButtonLabel: translations.submitButton,
      confirmButtonLabel: translations.confirmButton,
      addScreenshotButtonLabel: translations.addScreenshotButton,
      removeScreenshotButtonLabel: translations.removeScreenshotButton,
      nameLabel: translations.nameLabel,
      namePlaceholder: translations.namePlaceholder,
      emailLabel: translations.emailLabel,
      emailPlaceholder: translations.emailPlaceholder,
      messageLabel: translations.messageLabel,
      messagePlaceholder: translations.messagePlaceholder,
      isRequiredLabel: '',
      successMessageText: translations.successMessageText,
    } as Partial<FeedbackInternalOptions>
  }

  protected async getTranslations() {
    const [
      title,
      cancelButton,
      submitButton,
      confirmButton,
      addScreenshotButton,
      removeScreenshotButton,
      nameLabel,
      namePlaceholder,
      emailLabel,
      emailPlaceholder,
      messageLabel,
      messagePlaceholder,
      successMessageText,
    ] = await Promise.all([
      this.languageService.translate('feedback.title'),
      this.languageService.translate('feedback.cancel-button'),
      this.languageService.translate('feedback.submit-button'),
      this.languageService.translate('feedback.confirm-button'),
      this.languageService.translate('feedback.add-screenshot-button'),
      this.languageService.translate('feedback.remove-screenshot-button'),
      this.languageService.translate('feedback.name-label'),
      this.languageService.translate('feedback.name-placeholder'),
      this.languageService.translate('feedback.email-label'),
      this.languageService.translate('feedback.email-placeholder'),
      this.languageService.translate('feedback.message-label'),
      this.languageService.translate('feedback.message-placeholder'),
      this.languageService.translate('feedback.success-message-text'),
    ])
    return {
      title,
      cancelButton,
      submitButton,
      confirmButton,
      addScreenshotButton,
      removeScreenshotButton,
      nameLabel,
      namePlaceholder,
      emailLabel,
      emailPlaceholder,
      messageLabel,
      messagePlaceholder,
      successMessageText,
    }
  }

  protected readonly onFeedbackFormOpened = () => {
    const sentryRootElements = Array.from(
      document.querySelectorAll<HTMLElement>('#sentry-feedback'),
    )
    if (sentryRootElements.length) {
      const targetElement = sentryRootElements.at(-1)
      if (targetElement) {
        const style = document.createElement('style')
        style.innerHTML = sentryFeedbackCSSOverrides
        style.innerHTML = sentryFeedbackCSSOverrides
        targetElement.shadowRoot?.appendChild(style)
      }

      const elementsToRemove = sentryRootElements.slice(0, sentryRootElements.length - 1)
      if (elementsToRemove.length) {
        elementsToRemove.forEach((el) => {
          try {
            el.parentNode?.removeChild(el)
          } catch {
            return
          }
        })
      }
    }
  }
}

const sentryFeedbackCSSOverrides = `
  .dialog {
    --form-width: 312px;
    --input-border: 1px solid #d0d5dd;
    --button-border: 1px solid #d0d5dd;

    font-family: 'Switzer', sans-serif;
    backdrop-filter: blur(3px);
    background-color: rgba(16, 24, 40, 0.6);
  }

  @supports (height: 100dvh) {
    .dialog {
      max-height: 100dvh;
    }
  }

  .dialog__content {
    gap: 0;
    padding: 0;
  }

  .dialog__header {
    padding: 24px;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;
    color: rgba(16, 24, 40, 1);
    border-bottom: 1px solid var(--Gray-200, #e2e7ee);
  }

  form.form {
    padding: 24px;
  }

  .form__top {
    gap: 16px;
  }

  .form__label__text,
  .form__label__text--required {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: rgba(16, 24, 40, 1);
  }

  .btn-group {
    display: flex;
    flex-direction: row-reverse;
    justify-content: stretch;
    padding: 0 2px;
  }

  .btn-group .btn {
    width: 48%;
  }

  .btn-group button.btn--default {
    --button-color: var(--color-primary-500);
  }

  label[for='screenshot'] button {
    font-weight: 600;
    color: var(--accent-background);
  }

  .editor {
    background-color: white;
    background-image: unset;
  }

  .editor__crop-btn-group {
    width: fit-content;
  }

  @media all and (min-width: 480px) {
    .dialog__content {
      min-width: 360px;
    }
  }
`
